import React from 'react';

interface FAQProps {
  onTeamModalOpen?: () => void;
}

const FAQ: React.FC<FAQProps> = ({ onTeamModalOpen }) => {
  const faqs = [
    {
      question: "How can the numbers work so well?",
      answer: "It also raises the question—why do traditional systems fail us so badly? The answer is simple: when people work together toward a shared goal, things become easier. By removing middlemen, inefficiencies, and hidden costs, we create a leaner, more transparent system. With blockchain, we cut the excess—and build something healthier for everyone."
    },
    {
      question: "Am I buying real estate? Do I get equity in the properties?",
      answer: "No. Your Experience Key grants you access and utility within the community, not direct ownership or equity in any physical real estate. The model is designed to provide access to experiences and community spaces, separating it from traditional property investment."
    },
    {
      question: "What are the risks involved?",
      answer: "Like any web3 initiative, BornFree carries inherent risks—such as smart contract vulnerabilities, market fluctuations impacting DAO treasury assets, and the complexities of property acquisition and upkeep. To mitigate these, BornFree relies on audited contracts, straightforward transactions, and transparent reserves managed by the DAO. Stable decentralized systems are used alongside trusted traditional entities when needed. Ultimately, the project's success depends on engaged community participation and responsible governance."
    },
    {
      question: "How does the DAO generate revenue?",
      answer: "A 1% fee on all Keys made and sold help cover Born Free's core operations—including team compensation, platform maintenance, legal and admin costs, marketing, and DAO coordination. It also supports our Social Purpose Corporation (SPC), which ensures compliance and manages interactions with traditional financial and legal systems. This keeps the platform sustainable and community-powered."
    },
    {
      question: "Who manages the properties?",
      answer: "The SPC partners with professional local property management companies and onsite staff to handle day-to-day operations, maintenance, and guest services. The selection of these partners may be informed by community input and DAO voting, which serves as advisory guidance to the SPC."
    },
    {
      question: "What happens if a property becomes unprofitable or needs major repairs?",
      answer: "The SPC may consider community input and DAO voting as advisory guidance when making decisions about property sales. Any proceeds from property sales would be managed by the SPC and could be used to support future properties or community projects. Each property also includes an initial reserve fund, intended to cover major or unexpected expenses over time."
    },
    {
      question: "What is an Experience Key?",
      answer: "An Experience Key is your membership pass to the BornFree ecosystem. It grants you an annual allotment of credits, voting rights in the DAO, and priority access to reserve stays at community-funded properties."
    },
    {
      question: "What is a Reservation Key?",
      answer: "A Reservation Key is your confirmation dates, details and access key for entry. Cancelling or rescheduling also happens on chain directly by members. Access property info, calender and make changes on chain instantly."
    },
    {
      question: "How does the Round Robin system work for reservations?",
      answer: "Each year, members rotate in order to select from the top 200 date slots. Your place in line is based on your Key tier and time of making, giving everyone a chance to book popular dates over time."
    },
    {
      question: "Why merge credits?",
      answer: "Merging credits of different Round Robin numbers (RR#) helps reserve extended stays and could improve RR# by weighted averages."
    },
    {
      question: "How do I choose or change a reservation?",
      answer: "On-chain member reservations: All property info, calendars, and units are accessible and booked directly on-chain. Refunds: 60+ days = 100%, under 60 days = 90% minus 1% per day, under 7 days = 0%. Members can still sell on the Marketplace."
    },
    {
      question: "What are credits?",
      answer: "Credits power the Born Free ecosystem. When members make an Experience Key, they buy credits (200–20,000) at a 1:1 USDC rate. Credits replenish yearly, are used to make Reservation Keys and more, and expire if unused. Each credit has a Round Robin number (RR#) that sets booking priority—and credits can be traded, sold, or merged to optimize access and flexibility."
    },
    {
      question: "What happens to my credits at the end of the year?",
      answer: "Credits are issued each year and meant to be used within that year. Any unused credits are burned at the end of the cycle. This keeps access dynamic, encourages participation, and helps maintain a balanced system aligned with community use."
    },
    {
      question: "Can I sell my Experience Key or Reservation Key?",
      answer: "Yes — both Experience Keys and Reservation Keys can be sold or transferred, but only within the BornFree marketplace. This ensures fair pricing and prevents speculative reselling necessary for regulatory compliance."
    },
    {
      question: "What happens on the Born Free Marketplace?",
      answer: "The Born Free Marketplace lets members buy, sell, trade, and merge both credits and Keys. You can trade credits with different Round Robin priorities, merge to improve access, or sell and make Keys as needs change. This creates a flexible, transparent system where every action supports both personal goals and the community."
    },
    {
      question: "What legal structure does the DAO have?",
      answer: "While the DAO operates on-chain as a community advisory body, a legally recognized Social Purpose Corporation (SPC) interfaces with traditional legal and financial systems—such as signing property contracts. This structure, guided by legal advisors, ensures compliance. The SPC considers DAO community input as advisory guidance reflecting member preferences. All DAO activity remains transparent and on-chain for community visibility."
    },
    {
      question: "Why is this better than traditional ways of forming community?",
      answer: "Born Free reimagines community through Keys and blockchain, building flexible, transparent, and enduring ecosystems. Unlike traditional structures that fade over time, on-chain Keys preserve access and utility for future generations. Members can trade, merge, or sell credits, benefit from DAO governance, and support ongoing maintenance through built-in reserves—enabling communities to grow and thrive without centralized control or legal fragility."
    },
    {
      question: "Who is the team behind this project?",
      answer: "The DAO provides advisory input and community sentiment to inform the Born Free Community's direction. The SPC makes final decisions in compliance with legal requirements. This is the team developing the platform and interfacing with the legacy systems in our current paradigm."
    }
  ];

  const filteredFaqs = faqs.filter(faq => faq.question && faq.answer);
  const midpoint = Math.ceil(filteredFaqs.length / 2);
  const leftFaqs = filteredFaqs.slice(0, midpoint);
  const rightFaqs = filteredFaqs.slice(midpoint);

  return (
    <>
      <style>{`
        .faq-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0 3rem;
        }
        @media (min-width: 768px) {
          .faq-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>
      <section id="faq" style={{ backgroundColor: '#adadad', scrollMarginTop: '120px', padding: '5rem 0' }}>
        <div className="max-w-7xl mx-auto" style={{ paddingLeft: 'calc(80px + 1.5rem)', paddingRight: 'calc(80px + 1.5rem)' }}>
          <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#000' }}>Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div>
              {leftFaqs.map((faq, index) => (
                <div key={index} style={{ marginBottom: '2rem' }}>
                  <h3 className="text-lg font-bold mb-3" style={{ color: '#000' }}>{faq.question}</h3>
                  <div className="text-sm" style={{ color: '#000', lineHeight: '1.6' }}>
                    {faq.question === "Who is the team behind this project?" ? (
                      <>
                        {faq.answer}
                        <button
                          onClick={onTeamModalOpen}
                          className="text-blue-600 hover:text-blue-800 font-semibold underline ml-1"
                        >
                          Meet the Team
                        </button>
                      </>
                    ) : faq.question === "Can I sell my Experience Key or Reservation Key?" ? (
                      <div>
                        <p style={{ marginBottom: '8px' }}>
                          Yes — both Experience Keys and Reservation Keys can be sold or transferred, but only within the BornFree marketplace. This ensures fair pricing and prevents speculative reselling necessary for regulatory compliance.
                        </p>
                        <ul style={{ paddingLeft: '20px', listStyleType: 'disc' }}>
                          <li style={{ marginBottom: '4px' }}>
                            Experience Keys may be sold at a capped price based on platform-defined formulas that account for credit utility, inflation adjustments, and reservation priority.
                          </li>
                          <li>
                            Reservation Keys can be sold for the number of credits they were made with or USDC per market price.
                          </li>
                        </ul>
                      </div>
                    ) : (
                      faq.answer
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div>
              {rightFaqs.map((faq, index) => (
                <div key={index} style={{ marginBottom: '2rem' }}>
                  <h3 className="text-lg font-bold mb-3" style={{ color: '#000' }}>{faq.question}</h3>
                  <div className="text-sm" style={{ color: '#000', lineHeight: '1.6' }}>
                    {faq.question === "Who is the team behind this project?" ? (
                      <>
                        {faq.answer}
                        <button
                          onClick={onTeamModalOpen}
                          className="text-blue-600 hover:text-blue-800 font-semibold underline ml-1"
                        >
                          Meet the Team
                        </button>
                      </>
                    ) : faq.question === "Can I sell my Experience Key or Reservation Key?" ? (
                      <div>
                        <p style={{ marginBottom: '8px' }}>
                          Yes — both Experience Keys and Reservation Keys can be sold or transferred, but only within the BornFree marketplace. This ensures fair pricing and prevents speculative reselling necessary for regulatory compliance.
                        </p>
                        <ul style={{ paddingLeft: '20px', listStyleType: 'disc' }}>
                          <li style={{ marginBottom: '4px' }}>
                            Experience Keys may be sold at a capped price based on platform-defined formulas that account for credit utility, inflation adjustments, and reservation priority.
                          </li>
                          <li>
                            Reservation Keys can be sold for the number of credits they were made with or USDC per market price.
                          </li>
                        </ul>
                      </div>
                    ) : (
                      faq.answer
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;